/**
 * ContractorDataTab — F11 T11.2b zakładka „Dane".
 *
 * Silnik formatek (keystone unifikacja-formatek, ostatni krok migracji — mirror
 * `ContractDetail`/`LetterDetail`: zakładka „Dane" z układu przez silnik formatek):
 * 27 pól core routowanych przez
 * `renderField(fieldId, settings)` z propsów, dostarczany przez rodzica
 * (`ContractorDetail`) z `useKartotekaRenderField(contractorEngineConfig, runtime)`.
 * Ten komponent pobiera TYLKO aktywny layout modułu Kontrahenci przez
 * `useSystemModuleActiveLayout('contractors')` (ten sam hook co rodzic — honoruje
 * layout przypięty w pozycji menu `?mi=`), iteruje po sekcjach z
 * `layout_config.tabs[0].sections` (zakładka „Dane" type='fields') i dla
 * każdego elementu typu 'field' deleguje do `renderField` (silnik sam
 * rozstrzyga core-vs-ext, metadane pola, gcrecord, custom widget vs natywna
 * delegacja) — DataTab nie zna już `SystemModuleField`/`fieldsQ`.
 *
 * Hardkoded sekcja „Tagi i kody PKD" na końcu — `tags` + `pkd_codes` to TEXT[]
 * outliers (nie pasują do 12-typowego whitelist `fieldType`, brak wpisu w
 * katalogu `system_module_fields` — poza silnikiem).
 *
 * Save flow w ContractorDetail (rodzic): JEDEN POST/PATCH /contractors z `extValues`
 * (i `additionalTables`) w tym samym body — bez osobnego PUT ext-fields.
 */

import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ContractorObjectType,
  ContractorStatus,
  ContractorVatStatus,
  RecordLanguage,
} from '@saas/shared';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { type ContextRenderers, PLACEHOLDER_TYPES } from '@/components/form/RecursiveFormRenderer';
import { isFieldElementVisible, StyledFieldSlot } from '@/components/form/styled-field';
import { Field, Section } from '@/components/kartoteki/CoreFieldLayout';
import { Skeleton } from '@/components/Skeleton';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { loadTabsFromConfig } from '@/pages/system-modules/layout-shape-adapter';

const MODULE_SLUG = 'contractors';

// ─── Type exports (zachowane z F11 — używane w ContractorDetail) ────────────

/**
 * Adres macierzysty (siedziba/rejestrowy) kontrahenta — WF NEW 2026-08-13 (punkt 1).
 * Jeden rekord, edytowalny w zakładce Dane i w zakładce Adresy; zapisywany do polimorficznej
 * tabeli adresów (typ `office`, `isPrimary=true`) razem z nagłówkiem kontrahenta.
 * Klucze MUSZĄ pokrywać się 1:1 z `PRIMARY_ADDRESS_SCHEMA` w backendzie (schemat `.strict()`).
 */
export interface ContractorPrimaryAddress {
  street: string | null;
  buildingNo: string | null;
  apartmentNo: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
}

export const EMPTY_PRIMARY_ADDRESS: ContractorPrimaryAddress = {
  street: null,
  buildingNo: null,
  apartmentNo: null,
  postalCode: null,
  city: null,
  region: null,
  countryCode: null,
};

export interface ContractorFormData {
  name: string;
  /**
   * Firma-właściciel (`home_company_id`) — WYŁĄCZNIE pole create (patrz `company_id` w
   * `CONTRACTOR_RENDERERS`, `contractorEngineConfig.tsx`, disabled poza `isCreate`). Pusty
   * string = brak wyboru (wymagane w trybie "Wszystkie firmy", prefillowane automatycznie w
   * trybie jednej aktywnej firmy — patrz `ContractorDetail.tsx`). Nazwa stanu dopasowana do
   * pola body `POST /contractors` (`targetCompanyId`), żeby spread `{ ...data }` zadział bez
   * mapowania.
   */
  targetCompanyId: string;
  shortName: string | null;
  objectType: ContractorObjectType;
  taxId: string | null;
  vatEu: string | null;
  regon: string | null;
  krs: string | null;
  status: ContractorStatus;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  website: string | null;
  currencyCode: string;
  paymentTermsDays: number | null;
  creditLimit: string | null;
  isVatPayer: boolean | null;
  vatStatus: ContractorVatStatus | null;
  ownerUserId: string | null;
  assignedUserId: string | null;
  source: string | null;
  /** Wyłączony z synchronizacji ERP — true = pomijany w pull-u z Symfonii. Edytowalne (przełącznik usera). */
  syncExcluded: boolean;
  /** ID rekordu w Symfonii (external_id z powiązania) — ODRĘBNE od Pozycji. READ-ONLY (flaga katalogu). */
  symfoniaExternalId: string | null;
  /** Pozycja w Symfonii (FK) — stały numeryczny identyfikator z synchronizacji. READ-ONLY (flaga katalogu). */
  symfoniaPosition: number | null;
  /** Aktywny po stronie Symfonii (FK Active) — status księgowy, ODRĘBNY od naszego `status`. READ-ONLY. */
  symfoniaActive: boolean | null;
  /**
   * Firma, dla której pokazujemy pozycję/status Symfonii (pozycja jest PER FIRMA — ADR-0039).
   * GET rozwiązuje ją z aktywnego X-Company-Id (fallback: firma macierzysta / pierwszy ref). Renderer
   * pokazuje jej nazwę, żeby było jasne, której firmy dotyczy wartość. null = brak powiązania Symfonii.
   */
  symfoniaCompanyId: string | null;
  industry: string | null;
  preferredLanguage: RecordLanguage;
  gdprConsentAt: string | null;
  gdprMarketingConsent: boolean;
  gdprDataRetentionUntil: string | null;
  notes: string | null;
  tags: string[];
  pkdCodes: string[];
  /**
   * Grupy rekordu — kody słownika `contractor_groups`, WIELE na rekord. Klasyfikacja do
   * filtrowania listy (stara kategoria LAYOUTOWA `system_module_categories` zdjęta z paska filtra
   * 2026-08-18; osobna oś kategorii-TYPU wróciła jako `categoryId` — patrz niżej, ADR-0093b).
   */
  groups: string[];
  /**
   * Kategoria rekordu — id z realnej tabeli `record_categories` (kartoteka_key='contractors',
   * migracja 0123), JEDNA na rekord. `null` = brak kategorii. Nowa oś klasyfikacji, DYSTYNKTYWNA
   * od `groups` (wiele, słownik) — patrz `RecordCategoryPicker`/`categoryRenderer`.
   */
  categoryId: string | null;
  /** Adres macierzysty (siedziba/rejestrowy) — edytowalny w Dane i Adresy, zapis do tabeli adresów. */
  primaryAddress: ContractorPrimaryAddress;
}

export const EMPTY_FORM: ContractorFormData = {
  name: '',
  targetCompanyId: '',
  shortName: null,
  objectType: 'company',
  taxId: null,
  vatEu: null,
  regon: null,
  krs: null,
  status: 'active',
  email: null,
  phone: null,
  mobile: null,
  website: null,
  currencyCode: 'PLN',
  paymentTermsDays: null,
  creditLimit: null,
  isVatPayer: null,
  vatStatus: null,
  ownerUserId: null,
  assignedUserId: null,
  source: null,
  syncExcluded: false,
  symfoniaExternalId: null,
  symfoniaPosition: null,
  symfoniaActive: null,
  symfoniaCompanyId: null,
  industry: null,
  preferredLanguage: 'pl',
  gdprConsentAt: null,
  gdprMarketingConsent: false,
  gdprDataRetentionUntil: null,
  notes: null,
  tags: [],
  pkdCodes: [],
  groups: [],
  categoryId: null,
  primaryAddress: { ...EMPTY_PRIMARY_ADDRESS },
};

/**
 * F22 (widoki dodatkowe) — słownik slug→stateKey dla pól core kontrahenta, mirror
 * `KartotekaEngineConfig.fields` dokumentów (`components/kartoteki/engine/engine-config.ts`).
 * Slugi MUSZĄ pokrywać się z `coreFields` w `apps/api/lib/seed-system-modules.ts` (moduł
 * `contractors`) — to te same slugi, które `useFieldsForModule('crm_core.contractors')`
 * oferuje jako kandydatów na wiązanie `record_field` w edytorze widoku dodatkowego
 * (`AdditionalViewFormModal.tsx`). `tags`/`pkdCodes` celowo pominięte — nie mają odpowiednika
 * w katalogu `system_module_fields` (TEXT[] outliers, patrz komentarz w seedzie), więc nie są
 * oferowane jako kandydaci.
 */
export const CONTRACTOR_CORE_FIELD_SLUGS: Record<string, keyof ContractorFormData> = {
  name: 'name',
  company_id: 'targetCompanyId',
  short_name: 'shortName',
  object_type: 'objectType',
  tax_id: 'taxId',
  vat_eu: 'vatEu',
  regon: 'regon',
  krs: 'krs',
  status: 'status',
  email: 'email',
  phone: 'phone',
  mobile: 'mobile',
  website: 'website',
  currency_code: 'currencyCode',
  payment_terms_days: 'paymentTermsDays',
  credit_limit: 'creditLimit',
  is_vat_payer: 'isVatPayer',
  vat_status: 'vatStatus',
  owner_user_id: 'ownerUserId',
  assigned_user_id: 'assignedUserId',
  source: 'source',
  industry: 'industry',
  preferred_language: 'preferredLanguage',
  gdpr_consent_at: 'gdprConsentAt',
  gdpr_marketing_consent: 'gdprMarketingConsent',
  gdpr_data_retention_until: 'gdprDataRetentionUntil',
  notes: 'notes',
  // Grupy ze słownika — pole systemowe `groups` (TEXT[] kodów). W mapie, bo silnik formatek
  // buduje zestaw pól WŁAŚNIE z niej; bez wpisu renderer grup nigdy by się nie wywołał.
  groups: 'groups',
  // Kategoria — pole systemowe `category_id` (realna tabela `record_categories`). Analogicznie
  // do `groups`: bez wpisu tutaj `categoryRenderer` nigdy by się nie wywołał.
  category_id: 'categoryId',
  // Integracja Symfonia (F18/FK) — pozycja + status = read-only (flaga katalogu), wyłączenie =
  // edytowalne. Bez wpisów tutaj silnik nie wołałby ich rendererów (wcześniej zaszyty baner/checkbox).
  symfonia_external_id: 'symfoniaExternalId',
  symfonia_position: 'symfoniaPosition',
  symfonia_active: 'symfoniaActive',
  sync_excluded: 'syncExcluded',
};

/**
 * Buduje `Record<slug, wartość>` z aktualnego formularza kontrahenta — źródło `coreRecordValues`
 * dla `buildAdditionalViewRecordValues` (`lib/additional-views/record-values.ts`), wołane z
 * `ContractorDetail.tsx` obok `extValues`. Zwraca `unknown` (nie od razu JSON-safe) — koercja do
 * string/number/boolean/null jest zadaniem `buildAdditionalViewRecordValues`, nie tej funkcji.
 */
export function buildContractorCoreRecordValues(form: ContractorFormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [slug, key] of Object.entries(CONTRACTOR_CORE_FIELD_SLUGS)) {
    result[slug] = form[key];
  }
  return result;
}

// ─── Types dla fetch'owanych danych ─────────────────────────────────────────

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /** Ukrycie przez nakładkę per-tenant (systemowy layout) — pomijane na karcie. */
  hidden?: boolean;
  settings?: Record<string, unknown>;
}
interface LayoutSection {
  id: string;
  title: string | null;
  columns?: number;
  elements: LayoutElement[];
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface ContractorDataTabProps {
  form: ContractorFormData;
  onChange: (next: ContractorFormData) => void;
  editable: boolean;
  /**
   * Renderery elementów zależnych od kontekstu (record_links_view/attachments_view/…),
   * te same, które ContractorDetail przekazuje do RecursiveFormRenderer. Bez nich taki
   * element postawiony w zakładce „Dane” renderuje się jako martwy placeholder
   * z generycznego rejestru. Most do czasu przejścia kartoteki w całości na silnik.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Renderuje pole core/ext po id — dostarczany przez rodzica z
   * `useKartotekaRenderField(contractorEngineConfig, runtime)`. Silnik sam
   * rozstrzyga core-vs-ext, metadane pola (gcrecord/isCore/isRequired) i custom
   * widget vs natywną delegację — ten komponent tylko iteruje po layoutcie.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Wartości formularza keyed po slugu pola (core + ext) — do oceny warunków
   * widoczności pól/bloków (`settings.conditionalField/Operator/Value`). Rodzic
   * (`ContractorDetail`) buduje ją z `buildContractorCoreRecordValues(form)` +
   * `extValues`. Default `{}` — bez niej warunki traktujemy jak „zawsze widoczne".
   */
  formData?: Record<string, unknown>;
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function ContractorDataTab({
  form,
  onChange,
  editable,
  contextRenderers,
  renderField,
  formData = {},
}: ContractorDataTabProps) {
  const { t } = useTranslation();

  // Aktywny layout — TEN SAM hook co rodzic (`ContractorDetail`): honoruje layout
  // przypięty w pozycji menu (`?mi=` → `layoutId`) i sam nakłada nakładkę per-tenant
  // (`applyTabOverrides`). Metadane pól (fieldsQ) żyją w silniku (useKartotekaRenderField,
  // rodzic), ten komponent zna tylko strukturę sekcji.
  const layoutQ = useSystemModuleActiveLayout(MODULE_SLUG);

  const { dataTabSections, dataTabExtras } = useMemo(() => {
    // F12.N post-E1.T7: backend zwraca NOWY rekurencyjny kształt
    // (`elements[0].tabs[].children`). Adapter `loadTabsFromConfig` rozpakowuje
    // do starego płaskiego `LayoutTab[]` z polami `sections` + `extras`, których
    // ta strona używa do renderu (kompatybilność z F11 hardkodingiem).
    // Nakładkę per-tenant (ukryte pola/formatki, scalone settings) nakłada już hook
    // `useSystemModuleActiveLayout` — tu NIE aplikujemy jej drugi raz.
    const cfg = layoutQ.layoutConfig ?? { elements: [] };
    const tabs = loadTabsFromConfig(cfg);
    const dataTab = tabs.find((tab) => tab.type === 'fields');
    return {
      dataTabSections: (dataTab?.sections ?? []) as LayoutSection[],
      // Elementy „luźne" zakładki Dane spoza sekcji (blok Adres macierzysty seed
      // wstawia jako rodzeństwo sekcji, nie w sekcji). Renderujemy je PEŁNĄ
      // szerokością pod sekcjami — bez tego adapter wrzuca je do `extras`, a ta
      // strona milcząco je gubiła (formatka adresu w ogóle się nie pokazywała).
      dataTabExtras: (dataTab?.extras ?? []) as LayoutElement[],
    };
  }, [layoutQ.layoutConfig]);

  function set<K extends keyof ContractorFormData>(key: K, value: ContractorFormData[K]) {
    onChange({ ...form, [key]: value });
  }

  // Renderuje „luźny" element layoutu (blok-widok spoza sekcji, np. „Adres macierzysty")
  // PEŁNĄ szerokością: przez contextRenderers rodzica (adres/załączniki/powiązania…), a dla
  // elementów bez kontekstu — przez generyczny rejestr (heading/alert/…). Settings elementu
  // (`hiddenFields` itp.) lecą do renderera, więc konfiguracja z kreatora działa.
  function renderLooseElement(el: LayoutElement): ReactNode {
    const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
      ? contextRenderers?.[el.type]
      : undefined;
    if (contextRenderer) {
      return (
        <Fragment key={el.id}>
          {contextRenderer({ element: { id: el.id, type: el.type, settings: el.settings } })}
        </Fragment>
      );
    }
    const layoutNode = tryRenderLayoutElement({
      element: {
        id: el.id,
        type: el.type,
        settings: (el.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'form',
    });
    return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
  }

  if (layoutQ.isLoading) {
    return (
      <div className="space-y-3 p-4" data-testid="contractor-data-tab-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showLayoutWarning = layoutQ.isError || (!layoutQ.isLoading && dataTabSections.length === 0);

  return (
    <div className="space-y-6" data-testid="contractor-data-tab">
      {showLayoutWarning && (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="contractor-data-tab-no-layout"
        >
          {t('tenant.contractors.detail.noLayoutWarning')}
        </div>
      )}

      {dataTabSections.map((section, sectionIdx) => (
        <Fragment key={section.id}>
          <Section title={section.title ?? t('tenant.contractors.detail.untitledSection')}>
            {section.elements.map((el) => {
              // Ukrycie przez nakładkę per-tenant (systemowy layout) — pole/element z flagą
              // `hidden` nie renderuje się na karcie (builder pokazuje je wyszarzone).
              if (el.hidden === true) return null;
              // Warunek widoczności z kreatora (`settings.conditionalField/Operator/Value`
              // vs `formData`) — parytet z bogatym rendererem. Wcześniej pole warunkowe
              // było zawsze widoczne, bo bespoke sprawdzał tylko `hidden`.
              if (!isFieldElementVisible(el, formData)) return null;
              // Krok B (2026-05-14) — non-field elementy (heading/divider/alert/...)
              // renderowane przez LayoutElementRegistry. Side-effect import w App.tsx.
              if (el.type !== 'field') {
                // Elementy zależne od kontekstu (record_links_view/attachments_view/…) muszą
                // iść przez contextRenderers rodzica — dokładnie jak w silniku
                // (RecursiveFormRenderer) — inaczej renderują się jako martwy placeholder
                // z generycznego rejestru. Fallback: generyczny rejestr dla elementów bez
                // kontekstu (heading/divider/alert/…).
                const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
                  ? contextRenderers?.[el.type]
                  : undefined;
                if (contextRenderer) {
                  // Bloki-widoki (adres macierzysty, załączniki, powiązania…) to
                  // pełnowymiarowe formatki, nie pojedyncze pole — w gridzie sekcji
                  // muszą zajmować cały wiersz (`md:col-span-2`), inaczej ściska je
                  // do połowy szerokości i inputy robią się malutkie.
                  return (
                    <div key={el.id} className="md:col-span-2">
                      {contextRenderer({
                        element: { id: el.id, type: el.type, settings: el.settings },
                      })}
                    </div>
                  );
                }
                const layoutNode = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'form',
                });
                return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
              }

              if (!el.fieldId) return null;
              // Styl pola z kreatora (kolor etykiety/kontrolki) — scoped-CSS wokół
              // `renderField`, wspólny klocek z bogatym rendererem (styled-field).
              return (
                <StyledFieldSlot key={el.id} el={el} formData={formData}>
                  {renderField(el.fieldId, el.settings)}
                </StyledFieldSlot>
              );
            })}
          </Section>
          {/* Luźne elementy zakładki Dane spoza sekcji (blok „Adres macierzysty" seed
            wstawia jako rodzeństwo sekcji, zaraz po „Identyfikacji"). Renderujemy je
            PEŁNĄ szerokością TUŻ po pierwszej sekcji — inaczej adapter chowa je w
            `extras` i strona gubiła formatkę (a `settings.hiddenFields` = ukrywanie pól). */}
          {sectionIdx === 0 && dataTabExtras.map(renderLooseElement)}
        </Fragment>
      ))}
      {/* Nietypowy layout bez żadnej sekcji — luźne elementy i tak pokaż. */}
      {dataTabSections.length === 0 && dataTabExtras.map(renderLooseElement)}

      {/* Hardkoded outliers — tags + pkd_codes (TEXT[] poza whitelist 12 typów) */}
      <Section title={t('tenant.contractors.detail.section.tagsAndPkd')}>
        <Field label={t('tenant.contractors.detail.field.tags')} colSpan={2}>
          <TagsInput
            tags={form.tags}
            onChange={(tags) => set('tags', tags)}
            disabled={!editable}
            placeholder={t('tenant.contractors.detail.tagPlaceholder')}
          />
        </Field>
        <Field label={t('tenant.contractors.detail.field.pkdCodes')} colSpan={2}>
          <PkdCodesInput
            codes={form.pkdCodes}
            onChange={(codes) => set('pkdCodes', codes)}
            disabled={!editable}
          />
        </Field>
      </Section>
    </div>
  );
}

// ─── Hardkoded helpers — Tagi + Kody PKD (TEXT[] outliers) ─────────────────

function TagsInput({
  tags,
  onChange,
  disabled,
  placeholder,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  function add() {
    const v = draft.trim();
    if (!v || tags.includes(v)) return;
    onChange([...tags, v]);
    setDraft('');
  }

  function remove(tag: string) {
    onChange(tags.filter((x) => x !== tag));
  }

  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-border-secondary bg-bg-primary px-2 py-1.5">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded bg-accent-50 px-2 py-0.5 text-xs text-accent-700 dark:bg-accent-900/30 dark:text-accent-300"
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => remove(tag)}
              aria-label={t('tenant.contractors.detail.removeTag', { name: tag })}
            >
              ×
            </button>
          )}
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
        className="flex-1 min-w-[100px] bg-transparent text-sm outline-none"
      />
    </div>
  );
}

function PkdCodesInput({
  codes,
  onChange,
  disabled,
}: {
  codes: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const PKD_REGEX = /^\d{2}\.\d{2}\.[A-Z]$/;

  function add() {
    const v = draft.trim().toUpperCase();
    if (!v) return;
    if (!PKD_REGEX.test(v)) {
      setError(t('tenant.contractors.detail.pkdInvalid'));
      return;
    }
    if (codes.includes(v)) {
      setError(t('tenant.contractors.detail.pkdDuplicate'));
      return;
    }
    if (codes.length >= 20) {
      setError(t('tenant.contractors.detail.pkdMaxReached'));
      return;
    }
    onChange([...codes, v]);
    setDraft('');
    setError(null);
  }

  function remove(c: string) {
    onChange(codes.filter((x) => x !== c));
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1 rounded-lg border border-border-secondary bg-bg-primary px-2 py-1.5">
        {codes.map((code) => (
          <span
            key={code}
            className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-mono text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            data-testid={`contractor-pkd-chip-${code}`}
          >
            {code}
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(code)}
                aria-label={t('tenant.contractors.detail.pkdRemove', { code })}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          disabled={disabled}
          placeholder={t('tenant.contractors.detail.pkdPlaceholder')}
          className="flex-1 min-w-[120px] bg-transparent text-sm font-mono outline-none"
          data-testid="contractor-pkd-input"
        />
      </div>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid="contractor-pkd-error">
          {error}
        </p>
      )}
    </div>
  );
}
